import React, { useEffect, useState } from "react";
import ApiConfig from "../../common/config/ApiConfig";
import XLSX from "xlsx-js-style";
import { toast } from "react-toastify";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useLanguage } from "../../common/i18n/LanguageContext";

const lottoTypeOptions = [
  { labelKey: "table_lotto_thai", value: "th_gov" },
  { labelKey: "table_lotto_laos", value: "laos_gov" },
  { labelKey: "table_lotto_yeekee5", value: "Yeekee_5" },
  { labelKey: "table_lotto_yeekee10", value: "Yeekee_10" },
  { labelKey: "table_lotto_yeekee15", value: "Yeekee_15" },
];

const toNumber = (value) => {
  const num = parseFloat(value);
  return isNaN(num) ? 0 : num;
};

const formatAmount = (value) => {
  const num = parseFloat(value);
  return isNaN(num)
    ? ""
    : num.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
};

const amountClass = (value) => {
  const num = parseFloat(value);
  return `font-bold ${!isNaN(num) && num < 0 ? "text-red-600" : "text-blue-600"}`;
};

const sumField = (rows, field) =>
  rows.reduce((total, item) => total + toNumber(item[field]), 0);

// Tier group column defs for the Excel export — mirrors the on-screen
// table's colored group headers (bg-brand-blue-300/amber-300/sky-300/
// emerald-300 for the group band, the -200 shade for its 3 sub-headers).
const EXPORT_GROUPS = [
  {
    key: "senior",
    labelKey: "winlose_col_senior",
    headerColor: "7EBDFF",
    subColor: "B3D7FF",
    wlField: "seniorWl",
    commissionField: "seniorCommissionAmount",
    totalField: "seniorWlTotal",
  },
  {
    key: "master",
    labelKey: "winlose_col_master",
    headerColor: "FCD34D",
    subColor: "FDE68A",
    wlField: "masterWl",
    commissionField: "masterCommissionAmount",
    totalField: "masterWlTotal",
  },
  {
    key: "agent",
    labelKey: "winlose_col_agent",
    headerColor: "7DD3FC",
    subColor: "BAE6FD",
    wlField: "agentWl",
    commissionField: "agentCommissionAmount",
    totalField: "agentWlTotal",
  },
  {
    key: "member",
    labelKey: "common_member",
    headerColor: "6EE7B7",
    subColor: "A7F3D0",
    wlField: "memberWl",
    commissionField: "memberCommissionAmount",
    totalField: "memberWlTotal",
  },
];

const EXPORT_BORDER = { style: "thin", color: { rgb: "CBD5E1" } };
const EXPORT_THIN_BORDERS = {
  top: EXPORT_BORDER,
  bottom: EXPORT_BORDER,
  left: EXPORT_BORDER,
  right: EXPORT_BORDER,
};
const EXPORT_AMOUNT_FORMAT = "#,##0.00";
const amountFontColor = (value) => {
  const num = toNumber(value);
  return num < 0 ? "DC2626" : num > 0 ? "2563EB" : "1E293B";
};

// Builds the summary sheet with the same two-row grouped header shown on
// screen (Senior/Master/Agent/Member bands, each with W/L, Commission,
// Total sub-columns) — only the groups actually visible for this `pos`
// are included, matching showSenior/showMaster/showAgent/showMember.
const buildWinloseExportWorksheet = (
  t,
  rows,
  pos,
  visibleGroups,
  lottoTypeMappings,
  totals,
  memberTotals,
) => {
  const fixedColumns = [
    { label: `${t("winlose_col_account")} (${pos})` },
    { label: t("winlose_col_name") },
    { label: t("lotto_service_col_type") },
    { label: t("winlose_col_bet_amount") },
    { label: t("member_commission_label") },
    { label: t("winlose_col_payout") },
  ];
  const groups = EXPORT_GROUPS.filter((g) => visibleGroups.includes(g.key));
  const totalCols = fixedColumns.length + groups.length * 3;

  const headerRow0 = new Array(totalCols).fill("");
  const headerRow1 = new Array(totalCols).fill("");
  fixedColumns.forEach((col, i) => {
    headerRow0[i] = col.label;
  });
  groups.forEach((group, i) => {
    const start = fixedColumns.length + i * 3;
    headerRow0[start] = t(group.labelKey);
    headerRow1[start] = t("winlose_col_win_loss");
    headerRow1[start + 1] = t("member_commission_label");
    headerRow1[start + 2] = t("table_total");
  });

  const dataRows = rows.map((row) => {
    const rowCommission =
      pos === "agent"
        ? toNumber(row.memberCommissionAmount)
        : toNumber(row.seniorCommissionAmount) +
          toNumber(row.masterCommissionAmount) +
          toNumber(row.agentCommissionAmount);
    const line = new Array(totalCols).fill("");
    line[0] = row.username;
    line[1] =
      pos === "agent"
        ? [row.f_name, row.l_name].filter(Boolean).join(" ") || "-"
        : row.name || "-";
    line[2] = lottoTypeMappings[row.lottotype] || row.lottotype || "-";
    line[3] = toNumber(row.bet_amount);
    line[4] = toNumber(rowCommission);
    line[5] = toNumber(row.payrateAmount);
    groups.forEach((group, i) => {
      const start = fixedColumns.length + i * 3;
      line[start] = toNumber(row[group.wlField]);
      line[start + 1] = toNumber(row[group.commissionField]);
      line[start + 2] = toNumber(row[group.totalField]);
    });
    return line;
  });

  const totalsLine = new Array(totalCols).fill("");
  totalsLine[0] = t("table_total");
  totalsLine[3] = totals.bet;
  totalsLine[4] = totals.commission;
  totalsLine[5] = totals.payrate;
  groups.forEach((group, i) => {
    const start = fixedColumns.length + i * 3;
    if (group.key === "member") {
      totalsLine[start] = memberTotals.turnover;
      totalsLine[start + 1] = memberTotals.netCommission;
      totalsLine[start + 2] = memberTotals.sum;
    } else {
      totalsLine[start] = totals[`${group.key}Turnover`];
      totalsLine[start + 1] = totals[`${group.key}Commission`];
      totalsLine[start + 2] = totals[`${group.key}Sum`];
    }
  });

  const worksheet = XLSX.utils.aoa_to_sheet([
    headerRow0,
    headerRow1,
    ...dataRows,
    totalsLine,
  ]);

  const merges = [];
  fixedColumns.forEach((_, c) => {
    merges.push({ s: { r: 0, c }, e: { r: 1, c } });
  });
  groups.forEach((_, i) => {
    const start = fixedColumns.length + i * 3;
    merges.push({ s: { r: 0, c: start }, e: { r: 0, c: start + 2 } });
  });
  const totalsRowIndex = dataRows.length + 2;
  merges.push({
    s: { r: totalsRowIndex, c: 0 },
    e: { r: totalsRowIndex, c: 2 },
  });
  worksheet["!merges"] = merges;

  worksheet["!cols"] = fixedColumns
    .map(() => ({ wch: 16 }))
    .concat(groups.flatMap(() => [{ wch: 14 }, { wch: 14 }, { wch: 14 }]));

  const setStyle = (r, c, style) => {
    const ref = XLSX.utils.encode_cell({ r, c });
    if (!worksheet[ref]) worksheet[ref] = { t: "s", v: "" };
    worksheet[ref].s = { ...(worksheet[ref].s || {}), ...style };
  };

  const headerStyle = (bgColor) => ({
    font: { bold: true, color: { rgb: "1E293B" } },
    alignment: { horizontal: "center", vertical: "center", wrapText: true },
    border: EXPORT_THIN_BORDERS,
    fill: { fgColor: { rgb: bgColor } },
  });

  fixedColumns.forEach((_, c) => {
    setStyle(0, c, headerStyle("BAE6FD"));
    setStyle(1, c, headerStyle("BAE6FD"));
  });
  groups.forEach((group, i) => {
    const start = fixedColumns.length + i * 3;
    for (let c = start; c < start + 3; c++) {
      setStyle(0, c, headerStyle(group.headerColor));
      setStyle(1, c, headerStyle(group.subColor));
    }
  });

  const amountCols = new Set([3, 4, 5]);
  groups.forEach((_, i) => {
    const start = fixedColumns.length + i * 3;
    amountCols.add(start);
    amountCols.add(start + 1);
    amountCols.add(start + 2);
  });

  dataRows.forEach((line, rowIdx) => {
    const r = rowIdx + 2;
    for (let c = 0; c < totalCols; c++) {
      const style = { border: EXPORT_THIN_BORDERS };
      if (amountCols.has(c)) {
        style.alignment = { horizontal: "right" };
        style.numFmt = EXPORT_AMOUNT_FORMAT;
        style.font = { bold: true, color: { rgb: amountFontColor(line[c]) } };
      }
      setStyle(r, c, style);
    }
  });

  for (let c = 0; c < totalCols; c++) {
    const style = {
      border: EXPORT_THIN_BORDERS,
      font: { bold: true, color: { rgb: "0D4BB3" } },
    };
    if (amountCols.has(c)) {
      style.alignment = { horizontal: "right" };
      style.numFmt = EXPORT_AMOUNT_FORMAT;
      style.font = {
        bold: true,
        color: { rgb: amountFontColor(totalsLine[c]) },
      };
    }
    setStyle(totalsRowIndex, c, style);
  }

  return worksheet;
};

const WinloseTable = ({
  data,
  startdate,
  enddate,
  setstartdate,
  setenddate,
  fetch,
  lottoselect,
  setLottoselect,
  lottoTypeMappings,
  pos,
  seniorParam,
  masterParam,
  agentParam,
}) => {
  const { t } = useLanguage();
  const rows = Array.isArray(data) ? data : [];
  const [currencyCodeById, setCurrencyCodeById] = useState({});

  useEffect(() => {
    ApiConfig.get("/getCurrencyList").then((result) => {
      if (result.resCode !== 200) return;
      const list = Array.isArray(result.data) ? result.data : [];
      setCurrencyCodeById(Object.fromEntries(list.map((c) => [c.id, c.code])));
    });
  }, []);

  const rowCode = (row) => currencyCodeById[row.currency_id] ?? "";
  // The totals row sums every row, so it only gets a code when they all
  // share one currency (mixed currencies can't be added together).
  const currencyIds = new Set(rows.map((r) => r.currency_id));
  const totalsCode =
    currencyIds.size === 1 ? (currencyCodeById[[...currencyIds][0]] ?? "") : "";
  const showSenior = pos === "owner";
  const showMaster = pos === "owner" || pos === "senior";
  // At pos === "agent" the rows are members themselves — they carry
  // memberWl/memberCommissionAmount, not the agentWl/agentCommissionAmount
  // fields the agent group reads, so show member data there instead of
  // agent data.
  const showAgent = pos !== "agent";
  const showMember = true;

  const buildRowHref = (rowUsername) => {
    const nextParams = new URLSearchParams();
    nextParams.set("username", rowUsername);
    nextParams.set("startdate", startdate);
    nextParams.set("enddate", enddate);
    if (pos === "owner") {
      nextParams.set("senior", rowUsername);
    } else if (pos === "senior") {
      if (seniorParam) nextParams.set("senior", seniorParam);
      nextParams.set("master", rowUsername);
    } else if (pos === "master") {
      if (seniorParam) nextParams.set("senior", seniorParam);
      if (masterParam) nextParams.set("master", masterParam);
      nextParams.set("agent", rowUsername);
    } else {
      if (seniorParam) nextParams.set("senior", seniorParam);
      if (masterParam) nextParams.set("master", masterParam);
      if (agentParam) nextParams.set("agent", agentParam);
    }
    return `/winlose?${nextParams.toString()}`;
  };

  const buildViewHref = (rowUsername, dl) => {
    const params = new URLSearchParams();
    params.set("username", rowUsername);
    if (dl) params.set("dl", dl);
    params.set("startdate", startdate);
    params.set("enddate", enddate);
    lottoselect.forEach((lt) => params.append("lottotype", lt));
    return `/winlose-detail?${params.toString()}`;
  };

  const toggleLottoType = (value) => {
    if (lottoselect.includes(value)) {
      setLottoselect(lottoselect.filter((item) => item !== value));
    } else {
      setLottoselect([...lottoselect, value]);
    }
  };

  const totals = {
    bet: sumField(rows, "bet_amount"),
    payrate: sumField(rows, "payrateAmount"),
    commission:
      pos === "agent"
        ? sumField(rows, "memberCommissionAmount")
        : sumField(rows, "seniorCommissionAmount") +
          sumField(rows, "masterCommissionAmount") +
          sumField(rows, "agentCommissionAmount"),
    agentTurnover: sumField(rows, "agentWl"),
    agentCommission: sumField(rows, "agentCommissionAmount"),
    masterTurnover: sumField(rows, "masterWl"),
    masterCommission: sumField(rows, "masterCommissionAmount"),
    seniorTurnover: sumField(rows, "seniorWl"),
    seniorCommission: sumField(rows, "seniorCommissionAmount"),
  };
  totals.agentSum = sumField(rows, "agentWlTotal");
  totals.masterSum = sumField(rows, "masterWlTotal");
  totals.seniorSum = sumField(rows, "seniorWlTotal");

  const memberTotals = {
    turnover: sumField(rows, "memberWl"),
    netCommission: sumField(rows, "memberCommissionAmount"),
    sum: sumField(rows, "memberWlTotal"),
  };

  const [exporting, setExporting] = useState(false);
  const visibleGroups = [
    showSenior && "senior",
    showMaster && "master",
    showAgent && "agent",
    showMember && "member",
  ].filter(Boolean);

  // Exports exactly what's on screen — the rows already fetched for the
  // current filters (lotto type, date range, drill-down params) — so no
  // extra request is needed; /sumorder isn't paginated.
  const handleExportExcel = () => {
    if (rows.length === 0) {
      toast.info(t("common_no_data"), { autoClose: 2000 });
      return;
    }
    setExporting(true);
    try {
      const worksheet = buildWinloseExportWorksheet(
        t,
        rows,
        pos,
        visibleGroups,
        lottoTypeMappings,
        totals,
        memberTotals,
      );
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "WinLose");
      const filename = `winlose-${pos || "summary"}-${startdate}_to_${enddate}.xlsx`;
      XLSX.writeFile(workbook, filename);
    } catch (error) {
      toast.error(t("common_load_error"), { autoClose: 2000 });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-2">
      <div className="p-2 bg-amber-50 border-[1px] border-slate-400 rounded-lg text-[12px]">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <label className="font-bold">
              {t("table_filter_by_lotto_type")}
            </label>
            {lottoTypeOptions.map((item) => (
              <label key={item.value} className="flex items-center gap-1">
                <input
                  type="checkbox"
                  value={item.value}
                  checked={lottoselect.includes(item.value)}
                  onChange={(e) => toggleLottoType(e.target.value)}
                />
                {t(item.labelKey)}
              </label>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <label className="font-bold">{t("table_from_date")}</label>
            <input
              type="date"
              className="p-1 border border-gray-300 rounded text-[12px]"
              value={startdate}
              onChange={(e) => setstartdate(e.target.value)}
            />
            <span className="font-bold">-</span>
            <input
              type="date"
              className="p-1 border border-gray-300 rounded text-[12px]"
              value={enddate}
              onChange={(e) => setenddate(e.target.value)}
            />
          </div>
          <button
            onClick={() => fetch()}
            className="py-1 px-3 bg-blue-500 text-white rounded"
          >
            {t("table_search_button")}
          </button>
          <button
            onClick={handleExportExcel}
            disabled={exporting}
            className="flex items-center gap-1 py-1 px-3 bg-green-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FileDownloadIcon sx={{ fontSize: 18 }} />
            {exporting ? t("common_exporting") : t("common_export_excel")}
          </button>
        </div>
      </div>

      <div className="rounded overflow-hidden mt-1 overflow-x-auto">
        {(seniorParam || masterParam || agentParam) && (
          <div className="text-[12px] font-bold flex items-center gap-1 mb-1">
            {[
              seniorParam && {
                label: seniorParam,
                href: `/winlose?username=${seniorParam}&senior=${seniorParam}`,
              },
              masterParam && {
                label: masterParam,
                href: `/winlose?username=${masterParam}&senior=${seniorParam}&master=${masterParam}`,
              },
              agentParam && {
                label: agentParam,
                href: `/winlose?username=${agentParam}&senior=${seniorParam}&master=${masterParam}&agent=${agentParam}`,
              },
            ]
              .filter(Boolean)
              .map((crumb, index, arr) => (
                <React.Fragment key={crumb.href}>
                  <a
                    href={crumb.href}
                    className="text-blue-600 hover:underline"
                  >
                    {crumb.label}
                  </a>
                  {index < arr.length - 1 && (
                    <span className="text-gray-400">\</span>
                  )}
                </React.Fragment>
              ))}
          </div>
        )}
        <table className="w-full text-[12px] border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th rowSpan={2} className="py-0.5 px-1 text-[12px]">
                {t("winlose_col_account")} ({pos})
              </th>
              <th rowSpan={2} className="py-0.5 px-1 text-[12px]">
                {t("winlose_col_name")}
              </th>
              <th rowSpan={2} className="py-0.5 px-1 text-[12px]">
                {t("winlose_col_view")}
              </th>
              <th rowSpan={2} className="py-0.5 px-1 text-[12px]">
                {t("lotto_service_col_type")}
              </th>
              <th rowSpan={2} className="py-0.5 px-1 text-[12px] bg-sky-200">
                {t("winlose_col_bet_amount")}
              </th>
              <th rowSpan={2} className="py-0.5 px-1 text-[12px] bg-sky-200">
                {t("member_commission_label")}
              </th>
              <th rowSpan={2} className="py-0.5 px-1 text-[12px] bg-sky-200">
                {t("winlose_col_payout")}
              </th>
              {showSenior && (
                <th
                  colSpan={3}
                  className="py-0.5 px-1 text-[12px] bg-brand-blue-300"
                >
                  {t("winlose_col_senior")}
                </th>
              )}
              {showMaster && (
                <th
                  colSpan={3}
                  className="py-0.5 px-1 text-[12px] bg-amber-300"
                >
                  {t("winlose_col_master")}
                </th>
              )}
              {showAgent && (
                <th colSpan={3} className="py-0.5 px-1 text-[12px] bg-sky-300">
                  {t("winlose_col_agent")}
                </th>
              )}
              {showMember && (
                <th
                  colSpan={3}
                  className="py-0.5 px-1 text-[12px] bg-emerald-300"
                >
                  {t("common_member")}
                </th>
              )}
            </tr>
            <tr className="bg-gray-100">
              {showSenior && (
                <>
                  <th className="py-0.5 px-1 text-[12px] bg-brand-blue-200">
                    {t("winlose_col_win_loss")}
                  </th>
                  <th className="py-0.5 px-1 text-[12px] bg-brand-blue-200">
                    {t("member_commission_label")}
                  </th>
                  <th className="py-0.5 px-1 text-[12px] bg-brand-blue-200">
                    {t("table_total")}
                  </th>
                </>
              )}
              {showMaster && (
                <>
                  <th className="py-0.5 px-1 text-[12px] bg-amber-200">
                    {t("winlose_col_win_loss")}
                  </th>
                  <th className="py-0.5 px-1 text-[12px] bg-amber-200">
                    {t("member_commission_label")}
                  </th>
                  <th className="py-0.5 px-1 text-[12px] bg-amber-200">
                    {t("table_total")}
                  </th>
                </>
              )}
              {showAgent && (
                <>
                  <th className="py-0.5 px-1 text-[12px] bg-sky-200">
                    {t("winlose_col_win_loss")}
                  </th>
                  <th className="py-0.5 px-1 text-[12px] bg-sky-200">
                    {t("member_commission_label")}
                  </th>
                  <th className="py-0.5 px-1 text-[12px] bg-sky-200">
                    {t("table_total")}
                  </th>
                </>
              )}
              {showMember && (
                <>
                  <th className="py-0.5 px-1 text-[12px] bg-emerald-200">
                    {t("winlose_col_win_loss")}
                  </th>
                  <th className="py-0.5 px-1 text-[12px] bg-emerald-200">
                    {t("member_commission_label")}
                  </th>
                  <th className="py-0.5 px-1 text-[12px] bg-emerald-200">
                    {t("table_total")}
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const rowCommission =
                pos === "agent"
                  ? toNumber(row.memberCommissionAmount)
                  : toNumber(row.seniorCommissionAmount) +
                    toNumber(row.masterCommissionAmount) +
                    toNumber(row.agentCommissionAmount);
              return (
                <tr
                  key={row.username ?? index}
                  className={`transition-colors ${
                    index % 2 === 0 ? "bg-gray-50" : "bg-gray-200"
                  } hover:bg-gray-300`}
                >
                  <td className="p-1 text-center font-bold text-slate-700">
                    {pos === "agent" ? (
                      row.username
                    ) : (
                      <a href={buildRowHref(row.username)}>
                        <u>{row.username}</u>
                      </a>
                    )}
                  </td>
                  <td className="p-1 text-center font-bold text-slate-700">
                    {pos === "agent"
                      ? [row.f_name, row.l_name].filter(Boolean).join(" ") ||
                        "-"
                      : row.name || "-"}
                  </td>
                  <td className="p-1 text-center">
                    <a
                      href={buildViewHref(row.username, row.dl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-amber-600 inline-block px-3 py-1 text-center text-white rounded hover:bg-amber-800"
                    >
                      <span className="font-bold">{t("common_view")}</span>
                    </a>
                  </td>
                  <td className="p-1 text-center font-bold text-slate-500">
                    {lottoTypeMappings[row.lottotype] || row.lottotype || "-"}
                  </td>
                  <td className="p-1 text-right">
                    <span className={amountClass(row.bet_amount)}>
                      {formatAmount(row.bet_amount)} {rowCode(row)}
                    </span>
                  </td>
                  <td className="p-1 text-right">
                    <span className={amountClass(rowCommission)}>
                      {formatAmount(rowCommission)}
                    </span>
                  </td>
                  <td className="p-1 text-right">
                    <span className={amountClass(row.payrateAmount)}>
                      {formatAmount(row.payrateAmount)} {rowCode(row)}
                    </span>
                  </td>
                  {showSenior && (
                    <>
                      <td className="p-1 text-right">
                        <span className={amountClass(row.seniorWl)}>
                          {formatAmount(row.seniorWl)}
                        </span>
                      </td>
                      <td className="p-1 text-right">
                        <span
                          className={amountClass(row.seniorCommissionAmount)}
                        >
                          {formatAmount(row.seniorCommissionAmount)}
                        </span>
                      </td>
                      <td className="p-1 text-right">
                        <span className={amountClass(row.seniorWlTotal)}>
                          {formatAmount(row.seniorWlTotal)} {rowCode(row)}
                        </span>
                      </td>
                    </>
                  )}
                  {showMaster && (
                    <>
                      <td className="p-1 text-right">
                        <span className={amountClass(row.masterWl)}>
                          {formatAmount(row.masterWl)}
                        </span>
                      </td>
                      <td className="p-1 text-right">
                        <span
                          className={amountClass(row.masterCommissionAmount)}
                        >
                          {formatAmount(row.masterCommissionAmount)}
                        </span>
                      </td>
                      <td className="p-1 text-right">
                        <span className={amountClass(row.masterWlTotal)}>
                          {formatAmount(row.masterWlTotal)} {rowCode(row)}
                        </span>
                      </td>
                    </>
                  )}
                  {showAgent && (
                    <>
                      <td className="p-1 text-right">
                        <span className={amountClass(row.agentWl)}>
                          {formatAmount(row.agentWl)}
                        </span>
                      </td>
                      <td className="p-1 text-right">
                        <span
                          className={amountClass(row.agentCommissionAmount)}
                        >
                          {formatAmount(row.agentCommissionAmount)}
                        </span>
                      </td>
                      <td className="p-1 text-right">
                        <span className={amountClass(row.agentWlTotal)}>
                          {formatAmount(row.agentWlTotal)} {rowCode(row)}
                        </span>
                      </td>
                    </>
                  )}
                  {showMember && (
                    <>
                      <td className="p-1 text-right">
                        <span className={amountClass(row.memberWl)}>
                          {formatAmount(row.memberWl)}
                        </span>
                      </td>
                      <td className="p-1 text-right">
                        <span
                          className={amountClass(row.memberCommissionAmount)}
                        >
                          {formatAmount(row.memberCommissionAmount)}
                        </span>
                      </td>
                      <td className="p-1 text-right">
                        <span className={amountClass(row.memberWlTotal)}>
                          {formatAmount(row.memberWlTotal)} {rowCode(row)}
                        </span>
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
            <tr>
              <td colSpan={4} align="right">
                <span className="p-1 font-bold text-brand-blue-600">
                  {t("table_total")}{" "}
                </span>
              </td>
              <td className="p-1 text-right">
                <span className={amountClass(totals.bet)}>
                  {formatAmount(totals.bet)} {totalsCode}
                </span>
              </td>
              <td className="p-1 text-right">
                <span className={amountClass(totals.commission)}>
                  {formatAmount(totals.commission)}
                </span>
              </td>
              <td className="p-1 text-right">
                <span className={amountClass(totals.payrate)}>
                  {formatAmount(totals.payrate)} {totalsCode}
                </span>
              </td>
              {showSenior && (
                <>
                  <td className="p-1 text-right">
                    <span className={amountClass(totals.seniorTurnover)}>
                      {formatAmount(totals.seniorTurnover)}
                    </span>
                  </td>
                  <td className="p-1 text-right">
                    <span className={amountClass(totals.seniorCommission)}>
                      {formatAmount(totals.seniorCommission)}
                    </span>
                  </td>
                  <td className="p-1 text-right">
                    <span className={amountClass(totals.seniorSum)}>
                      {formatAmount(totals.seniorSum)} {totalsCode}
                    </span>
                  </td>
                </>
              )}
              {showMaster && (
                <>
                  <td className="p-1 text-right">
                    <span className={amountClass(totals.masterTurnover)}>
                      {formatAmount(totals.masterTurnover)}
                    </span>
                  </td>
                  <td className="p-1 text-right">
                    <span className={amountClass(totals.masterCommission)}>
                      {formatAmount(totals.masterCommission)}
                    </span>
                  </td>
                  <td className="p-1 text-right">
                    <span className={amountClass(totals.masterSum)}>
                      {formatAmount(totals.masterSum)} {totalsCode}
                    </span>
                  </td>
                </>
              )}
              {showAgent && (
                <>
                  <td className="p-1 text-right">
                    <span className={amountClass(totals.agentTurnover)}>
                      {formatAmount(totals.agentTurnover)}
                    </span>
                  </td>
                  <td className="p-1 text-right">
                    <span className={amountClass(totals.agentCommission)}>
                      {formatAmount(totals.agentCommission)}
                    </span>
                  </td>
                  <td className="p-1 text-right">
                    <span className={amountClass(totals.agentSum)}>
                      {formatAmount(totals.agentSum)} {totalsCode}
                    </span>
                  </td>
                </>
              )}
              {showMember && (
                <>
                  <td className="p-1 text-right">
                    <span className={amountClass(memberTotals.turnover)}>
                      {formatAmount(memberTotals.turnover)}
                    </span>
                  </td>
                  <td className="p-1 text-right">
                    <span className={amountClass(memberTotals.netCommission)}>
                      {formatAmount(memberTotals.netCommission)}
                    </span>
                  </td>
                  <td className="p-1 text-right">
                    <span className={amountClass(memberTotals.sum)}>
                      {formatAmount(memberTotals.sum)} {totalsCode}
                    </span>
                  </td>
                </>
              )}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WinloseTable;
